import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "antd";
import { X as ICON_X, ArrowRight as ICON_ARROW_RIGHT } from 'react-feather';
import { queries } from "api/api.constants";
import { useLanguage } from "assets/languages";
import GUIDELINE_RULES from "constants/guidelineRules";

interface Props {
  isVisible: boolean;
  letClose: () => void;
  token: string;
  rentalId: number;
  isCategoryBeautyBrand: boolean;
}

const FeedbacksModal = (props: Props) => {
  const FEEDBACK_IMAGE_BASE_URL = 'https://nmodelin20.s3.ap-northeast-2.amazonaws.com/posting/thumb-sm/';
  const BEAUTY_REELS_GUIDE_IMAGE_BASE_URL = 'https://nmodelin20.s3.ap-northeast-2.amazonaws.com/guide/reels/';
  const REJECT_REASON_COLLECTION_GROUP_NAME = {
    NOT_INCLUDED_REELS_PRODUCT: 'NOT_INCLUDED_REELS_PRODUCT',
    DETAIL_REELS_PRODUCT: 'DETAIL_REELS_PRODUCT',
    SUBTITLE: 'SUBTITLE',
  };

  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.component.postingCard,
    ...language.guidelineTitles,
  };
  const prohibitionValue_rejectReasonTitle_map = {
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.GRWM_AND_OTHER_PRODUCTS]: languageTexts.feedback_reason_title_4,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.MAKE_UP]: languageTexts.feedback_reason_title_5,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.TPO_MISMATCH]: languageTexts.feedback_reason_title_6,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.FINGERPRINTS]: languageTexts.feedback_reason_title_7,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.ENLARGE]: languageTexts.feedback_reason_title_8,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.SLIDESHOW]: languageTexts.feedback_reason_title_9,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.COMMERCIAL_CONTENTS]: languageTexts.feedback_reason_title_10,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.HDR]: languageTexts.feedback_reason_title_20,
    [GUIDELINE_RULES.OPTIONS_PROHIBITIONS_BEAUTY.DEADZONE]: languageTexts.feedback_reason_title_22,
  };

  const [isGuidelinesFetched, setIsGuidelinesFetched] = useState(false);
  const [rejectReasonData, setRejectReasonData] = useState({
    items: [],
    hashTags: [],
    hashTagLocation: null,
    errorMessage: null,
  });

  // 제품 정보 가져오기
  const { data: guidelines, refetch: refetchGuidelines } = useQuery({
    ...queries.guidelines.getGuidelines({
      brandToken: props.token,
      rentalId: props.rentalId,
    }),
    select: (data: any) => data?.data?.guides,
    enabled: (props.isVisible && props.token != null && props.rentalId != null) && !isGuidelinesFetched,
  });

  useEffect(() => {
    if (guidelines != null) {
      setIsGuidelinesFetched(true);
    }
  }, [guidelines]);

  useEffect(() => {
    if (isGuidelinesFetched) {
      const [guidelinesWithFeedbacks, hashTags, hashTagLocation] = refineGuidelines(guidelines);
      setRejectReasonData({
        items: guidelinesWithFeedbacks,
        hashTags: hashTags,
        hashTagLocation: hashTagLocation,
        errorMessage: null,
      });
    }
  }, [isGuidelinesFetched]);

  const refineGuidelines = (guidelines: any) => {
    // filter feedbacks and get hashTag infos
    const guidelinesWithFeedbacks = [];
    const notIncludedReelsGuidelinesWithFeedbacks = [];
    const detailReelsGuidelinesWithFeedbacks = [];
    const subtitleGuidelinesWithFeedbacks = [];
    const inspectionFeedbackSequenceGuidelinesWithFeedbacks = [];
    let hashTags;
    let hashTagLocation;

    for (let guideline of guidelines) {
      if (guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS) {
        hashTags = guideline.ruleValue;
      }
      if (guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION) {
        hashTagLocation = guideline.ruleValue;
      }

      // INSPECTION_FEEDBACK_SEQUENCE 별도 처리
      if (
        guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.INSPECTION_FEEDBACK_SEQUENCE &&
        guideline.feedbacks != null &&
        guideline.feedbacks.length > 0
      ) {
        inspectionFeedbackSequenceGuidelinesWithFeedbacks.push({
          groupName: guideline?.groupName ?? null,
          ruleValue: guideline?.ruleValue ?? null,
          value: guideline?.value ?? null,
          feedbackText: guideline.feedbacks[0].text ?? null,
          from: guideline.feedbacks[0].from ?? null,
          to: guideline.feedbacks[0].to ?? null,
          order: guideline.order ?? guideline.rule?.order,
        });
      } else if (guideline.feedbacks != null && guideline.feedbacks.length > 0) {
        const guidelineWithFeedback = {
          groupName: guideline?.groupName ?? null,
          ruleValue: guideline?.ruleValue ?? null,
          value: guideline?.value ?? null,
          feedbackText: guideline.feedbacks[0].text ?? null,
          from: guideline.feedbacks[0].from ?? null,
          to: guideline.feedbacks[0].to ?? null,
          order: guideline.order ?? guideline.rule?.order,
        };
        if (
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_PACKAGE ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_TEXTURE ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_COLOR ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_TRY ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_BEFORE_AFTER ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_SELFIE
        ) {
          if (guidelineWithFeedback.feedbackText === GUIDELINE_RULES.RESERVED_FEEDBACK_TEXT.NOT_INCLUDED) {
            notIncludedReelsGuidelinesWithFeedbacks.push(guidelineWithFeedback);
          } else {
            detailReelsGuidelinesWithFeedbacks.push(guidelineWithFeedback);
          }
        } else if (
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_OPENING ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_MIDDLE ||
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_CLOSING
        ) {
          subtitleGuidelinesWithFeedbacks.push(guidelineWithFeedback);
        } else {
          guidelinesWithFeedbacks.push(guidelineWithFeedback);
        }
      }
    }
    const reelsProductGroupNameOrderMap = {
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT]: 1,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_PACKAGE]: 2,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_TEXTURE]: 3,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_COLOR]: 4,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_TRY]: 5,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_BEFORE_AFTER]: 6,
      [GUIDELINE_RULES.GROUP_NAMES.REELS_PRODUCT_SELFIE]: 7,
    };

    // INSPECTION_FEEDBACK_SEQUENCE 그룹 추가
    if (inspectionFeedbackSequenceGuidelinesWithFeedbacks.length > 0) {
      guidelinesWithFeedbacks.unshift({
        groupName: GUIDELINE_RULES.GROUP_NAMES.INSPECTION_FEEDBACK_SEQUENCE,
        children: inspectionFeedbackSequenceGuidelinesWithFeedbacks,
      });
    }

    if (detailReelsGuidelinesWithFeedbacks.length > 0) {
      detailReelsGuidelinesWithFeedbacks.sort(
        (a, b) => reelsProductGroupNameOrderMap[a.groupName] - reelsProductGroupNameOrderMap[b.groupName],
      );
      guidelinesWithFeedbacks.unshift({
        groupName: REJECT_REASON_COLLECTION_GROUP_NAME.DETAIL_REELS_PRODUCT,
        children: detailReelsGuidelinesWithFeedbacks,
      });
    }

    if (notIncludedReelsGuidelinesWithFeedbacks.length > 0) {
      notIncludedReelsGuidelinesWithFeedbacks.sort(
        (a, b) => reelsProductGroupNameOrderMap[a.groupName] - reelsProductGroupNameOrderMap[b.groupName],
      );
      guidelinesWithFeedbacks.unshift({
        groupName: REJECT_REASON_COLLECTION_GROUP_NAME.NOT_INCLUDED_REELS_PRODUCT,
        children: notIncludedReelsGuidelinesWithFeedbacks,
      });
    }

    if (subtitleGuidelinesWithFeedbacks.length > 0) {
      const subtitleGroupNameOrderMap = {
        [GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_OPENING]: 1,
        [GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_MIDDLE]: 2,
        [GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_CLOSING]: 3,
      };
      subtitleGuidelinesWithFeedbacks.sort((a, b) => {
        if (a.groupName === b.groupName && a.order != null && b.order != null) {
          return a.order - b.order;
        }
        return subtitleGroupNameOrderMap[a.groupName] - subtitleGroupNameOrderMap[b.groupName];
      });
      guidelinesWithFeedbacks.push({
        groupName: REJECT_REASON_COLLECTION_GROUP_NAME.SUBTITLE,
        children: subtitleGuidelinesWithFeedbacks,
      });
    }

    // sort
    var groupNameOrderMap = {
      [GUIDELINE_RULES.GROUP_NAMES.INSPECTION_FEEDBACK_SEQUENCE]: 1,
      [REJECT_REASON_COLLECTION_GROUP_NAME.NOT_INCLUDED_REELS_PRODUCT]: 2,
      [REJECT_REASON_COLLECTION_GROUP_NAME.DETAIL_REELS_PRODUCT]: 3,
      [GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS]: 4,
      [GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS]: 5,
      [GUIDELINE_RULES.GROUP_NAMES.PREFERRED_VIDEO_TYPE]: 6,
      [GUIDELINE_RULES.GROUP_NAMES.VIDEO_DURATION]: 7,
      [REJECT_REASON_COLLECTION_GROUP_NAME.SUBTITLE]: 8,
      [GUIDELINE_RULES.GROUP_NAMES.CONTENT]: 9,
      [GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE]: 10,

      [GUIDELINE_RULES.GROUP_NAMES.AT_TAGS]: 11,
      [GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG]: 12,
      [GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS]: 13,
      [GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION]: 14,
      [GUIDELINE_RULES.GROUP_NAMES.CO_AUTHOR]: 15,

      [GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS]: 16,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_FULL]: 17,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_HALF]: 18,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_CLOSEUP]: 19,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_WITHOUT_FACE]: 20,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_FULL]: 21,
      [GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_CLOSEUP]: 22,
    };
    guidelinesWithFeedbacks.sort(
      (a, b) => groupNameOrderMap[a.groupName] - groupNameOrderMap[b.groupName],
    );

    return [guidelinesWithFeedbacks, hashTags, hashTagLocation];
  };

  const renderReelsProductRejectReason = (params: {
    groupName: string;
    feedbackText: string | null;
    idx: number;
  }) => {
    return (
      <dd key={params.idx} className="flex gap-[15px]">
        <img
          src={`${BEAUTY_REELS_GUIDE_IMAGE_BASE_URL}${params.groupName.toLowerCase()}.png`}
          alt={`example-image-${params.groupName}`}
          className="w-[100px] h-[100px] object-cover"
        />
        <div className="py-[10px] flex flex-col gap-[10px]">
          <div className="font-bold">{languageTexts[params.groupName]}</div>
          {(
            params.feedbackText != null &&
            params.feedbackText !== GUIDELINE_RULES.RESERVED_FEEDBACK_TEXT.NOT_INCLUDED 
          ) ? (
            <div>{params.feedbackText}</div>
          ) : null}
        </div>
      </dd>
    );
  };

  const renderTextRejectReason = (params: {
    title: string | React.ReactNode;
    description?: string | null;
    feedbackText?: string | null;
    idx: number;
  }) => {
    return (
      <dl key={params.idx} className="flex flex-col gap-2 pb-8">
        <dt className="font-bold">{params.title}</dt>
        {params.description != null && (
          <dd className="whitespace-pre-wrap">{params.description}</dd>
        )}
        {params.feedbackText != null && (
          <dd className="whitespace-pre-wrap">{params.feedbackText}</dd>
        )}
      </dl>
    );
  };
  
  const renderSubtitleRejectReason = (params: {
    groupName: string;
    description: string | null;
    idx: number;
  }) => {
    // @note 그룹 구분 다시 필요해질 경우 // const groupNameText = languageTexts[params.groupName]
    return <dd>{`${params.description ?? ''}`}</dd>;
  };

  const renderTagsRejectReason = (params: {
    title: string;
    feedbackTagsText: string | null;
    idx: number;
  }) => {
    const splittedTags = params.feedbackTagsText?.split(/\s+/) || [];
    return (
      <dl key={params.idx} className="flex flex-col gap-2 pb-8">
        <dt className="font-bold">{params.title}</dt>
        {splittedTags.map((tag, i) => (
          <div key={i}>{tag}</div>
        ))}
      </dl>
    );
  };

  return (
    <Modal
      wrapClassName="py-10"
      className="rounded-antd-modal"
      centered={true}
      open={props.isVisible}
      onOk={props.letClose}
      onCancel={props.letClose}
      footer={null}
      closable={false}
      zIndex={1090}
      width={1080}
    >
      <div className="select-none">
        <div className="flex flex-col bg-white rounded-2xl">
          <div className="flex flex-row justify-between px-7 py-7">
            <span className="font-bold text-2xl text-black">
              {languageTexts.index_text_1}
            </span>
            <div className="cursor-pointer" onClick={props.letClose}>
              <ICON_X size={32} />
            </div>
          </div>
          <hr />
          <div className="p-7">
            {rejectReasonData.items.map((datum, i) => {
              switch (datum.groupName) {
                case GUIDELINE_RULES.GROUP_NAMES.ATTACHMENT_RAW_VIDEO:
                  return renderTextRejectReason({
                    title: languageTexts.feedback_reason_title_21,
                    feedbackText: datum.feedbackText ?? '-',
                    idx: i,
                  });
                case REJECT_REASON_COLLECTION_GROUP_NAME.NOT_INCLUDED_REELS_PRODUCT:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.feedback_reason_title_1}
                      </dt>
                      {datum.children.map((child, j) => {
                        return renderReelsProductRejectReason({
                          groupName: child.groupName,
                          feedbackText: child.feedbackText,
                          idx: j,
                        });
                      })}
                    </dl>
                  );

                case REJECT_REASON_COLLECTION_GROUP_NAME.DETAIL_REELS_PRODUCT:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.feedback_reason_title_2}
                      </dt>
                      {datum.children.map((child, j) => {
                        return renderReelsProductRejectReason({
                          groupName: child.groupName,
                          feedbackText: child.feedbackText,
                          idx: j,
                        });
                      })}
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS:
                  return renderTextRejectReason({
                    title: languageTexts.feedback_reason_title_3,
                    description: datum.ruleValue ?? '-',
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS:
                case GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS_BEAUTY:
                  return renderTextRejectReason({
                    title:
                      prohibitionValue_rejectReasonTitle_map[
                        datum.ruleValue
                      ],
                    description: null,
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.PREFERRED_VIDEO_TYPE:
                  return renderTextRejectReason({
                    title: languageTexts.feedback_reason_title_17,
                    description: GUIDELINE_RULES.GetOptionTranslationMap(
                      datum.groupName,
                      datum.ruleValue,
                    ),
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.VIDEO_DURATION:
                  return renderTextRejectReason({
                    title: languageTexts.feedback_reason_title_11,
                    description: GUIDELINE_RULES.GetOptionTranslationMap(
                      datum.groupName,
                      datum.ruleValue,
                    ),
                    idx: i,
                  });

                case REJECT_REASON_COLLECTION_GROUP_NAME.SUBTITLE:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.feedback_reason_title_12}
                      </dt>
                      {datum.children.map((child, j) => {
                        return renderSubtitleRejectReason({
                          groupName: child.groupName,
                          description: child.ruleValue ?? '-',
                          idx: j,
                        });
                      })}
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.CONTENT:
                  return renderTextRejectReason({
                    title: languageTexts.feedback_reason_title_13,
                    description: datum.ruleValue ?? '-',
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.AT_TAGS:
                  if (props.isCategoryBeautyBrand) {
                    return renderTagsRejectReason({
                      title: languageTexts.feedback_reason_title_14,
                      feedbackTagsText: datum.feedbackText,
                      idx: i,
                    });
                  } else {
                    return renderTextRejectReason({
                      title: languageTexts.index_text_4,
                      description: datum.ruleValue ?? '-',
                      idx: i,
                    });
                  }

                case GUIDELINE_RULES.GROUP_NAMES.CO_AUTHOR:
                  return renderTagsRejectReason({
                    title: languageTexts.feedback_reason_title_16,
                    feedbackTagsText: datum.feedbackText,
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS:
                  return renderTextRejectReason({
                    title: props.isCategoryBeautyBrand
                      ? languageTexts.feedback_reason_title_15
                      : rejectReasonData.hashTagLocation === GUIDELINE_RULES.OPTIONS_HASH_TAG_LOCATION.POST
                      ? languageTexts.index_text_6
                      : languageTexts.index_text_8,
                    description: datum.ruleValue ?? '-',
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG:
                  return renderTextRejectReason({
                    title: languageTexts._index_text_5(),
                    description: null,
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION:
                  return renderTextRejectReason({
                    title:
                      datum.ruleValue === GUIDELINE_RULES.OPTIONS_HASH_TAG_LOCATION.POST
                        ? languageTexts.index_text_12
                        : languageTexts.index_text_10,
                    description: null,
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE:
                  return renderTextRejectReason({
                    title: languageTexts.index_text_2,
                    description: datum.ruleValue ?? '-',
                    idx: i,
                  });

                case GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.index_text_14}
                      </dt>
                      <dd>{datum.ruleValue ?? '-'}</dd>
                      <dd>
                        {languageTexts.index_text_1}:{' '}
                        {datum.feedbackText ?? '-'}
                      </dd>
                      {datum.from == null ? null : (
                        <dd>
                          <span>{languageTexts.index_text_17} </span>
                          <span>
                            <a
                              href={datum.from}
                              target="_blank"
                              className="underline text-orange-400"
                            >
                              {datum.from}
                            </a>
                          </span>
                        </dd>
                      )}
                      {datum.to == null ? null : (
                        <dd>
                          <span>{languageTexts.index_text_18} </span>
                          <span>
                            <a
                              href={datum.to}
                              target="_blank"
                              className="underline text-orange-400"
                            >
                              {datum.to}
                            </a>
                          </span>
                        </dd>
                      )}
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_FULL:
                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_HALF:
                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_CLOSEUP:
                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_WITHOUT_FACE:
                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_FULL:
                case GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_CLOSEUP:
                // REELS_PHOTO
                case GUIDELINE_RULES.GROUP_NAMES.REELS_BEAUTY_PRODUCT_FULL:
                case GUIDELINE_RULES.GROUP_NAMES.REELS_BEAUTY_PRODUCT_SELFIE:
                case GUIDELINE_RULES.GROUP_NAMES.REELS_BEAUTY_PRODUCT_TRY:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.index_text_19}
                        {/* --구분필요할경우-- {`사진이 가이드라인을 위반했습니다. (${PHOTO_GROUPNAME_DESCRIPTION_MAP[datum.groupName]})`} */}
                      </dt>
                      {datum.ruleValue == null ? null : (
                        <dd>{`${languageTexts.index_text_30}: ${datum.ruleValue ?? ''}`}</dd>
                      )}
                      <dd>
                        {languageTexts.index_text_1}:{' '}
                        {datum.feedbackText ?? ''}
                      </dd>

                      {datum.from == null ? null : (
                        <dd className="flex items-center mt-2.5">
                          <div className="w-[183px]">
                            <img className="w-full" src={FEEDBACK_IMAGE_BASE_URL + datum.from} />
                          </div>
                          {datum.to == null ? null : (
                            <>
                              <div className="w-[48px]">
                                <ICON_ARROW_RIGHT className="box-content p-3" />
                              </div>
                              <div className="w-[183px]">
                                <img className="w-full" src={FEEDBACK_IMAGE_BASE_URL + datum.to} />
                              </div>
                            </>
                          )}
                        </dd>
                      )}

                      {datum.from == null ? null : (
                        <dd className="flex text-center">
                          <div className="w-[183px]">
                            {languageTexts._index_text_21()}
                          </div>
                          {datum.to == null ? null : (
                            <>
                              <div className="w-[48px]" />
                              <div className="w-[183px]">
                                {languageTexts._index_text_22()}
                              </div>
                            </>
                          )}
                        </dd>
                      )}
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.GUIDE_IMAGE:
                  let image = JSON.parse(datum.ruleValue);
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.feedback_reason_title_18}
                      </dt>
                      <div className="flex flex-row gap-2">
                        {image == null ? null : (
                          <dd className="w-[33%]">
                            <img src={image.url} alt={image.description} />
                          </dd>
                        )}
                        {image == null ? null : (
                          <dd>
                            <span className="font-bold">
                              {image.description}
                            </span>
                          </dd>
                        )}
                      </div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.BRAND_LOGO:
                  let logo = JSON.parse(datum.ruleValue);
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">
                        {languageTexts.feedback_reason_title_19}
                      </dt>
                      <div className="flex flex-row">
                        {logo == null ? null : (
                          <dd className="w-[33%]">
                            <img src={logo.url} />
                          </dd>
                        )}
                      </div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.INSPECTION_FEEDBACK_SEQUENCE:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_23}</dt>
                      <div className="flex flex-col gap-[5px]">
                        {datum.children.map((child, idx) => {
                          const decodedData = JSON.parse(child.value);
                          return (
                            <dd key={`inspection-feedback-sequence-${idx}`}>
                              <div className="flex flex-row gap-[15px] w-full">
                                <div className="w-[120px] h-[215px]">
                                  <img className="w-full h-full" src={decodedData.url} />
                                </div>
                                <div className="flex flex-col gap-[10px] p-[10px]">
                                  <span className="font-bold text-[14px]">{decodedData.timestamp}</span>
                                  <span className="text-[14px]">{decodedData.msg}</span>
                                </div>
                              </div>
                            </dd>
                          );
                        })}
                      </div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_FONT:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_24}</dt>
                      <div className="flex flex-row">{datum.ruleValue}</div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.SUBTITLE_SIZE:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_25}</dt>
                      <div className="flex flex-row">{datum.ruleValue}</div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.BACKGROUND_MUSIC_EDIT:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_26}</dt>
                      <div className="flex flex-row">{datum.ruleValue}</div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.NARRATION_ADD:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_27}</dt>
                      <div className="flex flex-row">{datum.ruleValue}</div>
                    </dl>
                  );

                case GUIDELINE_RULES.GROUP_NAMES.NARRATION_EDIT:
                  return (
                    <dl key={i} className="flex flex-col gap-2 pb-8">
                      <dt className="font-bold">{languageTexts.feedback_reason_title_28}</dt>
                      <div className="flex flex-row">{datum.ruleValue}</div>
                    </dl>
                  );

                default:
                  return null;
              }
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default FeedbacksModal;
